import type { GetServerSidePropsContext } from 'next';
import type { ParsedUrlQuery } from 'querystring';
import dayjs from 'dayjs';
import type { DestroyCookie, SetCookie } from '@packages/utilities';

export const cookiePrefix = 'mvt_';
const participantSuffix = '_participant';

export const getTestCookieName = (testId: string) => `${cookiePrefix}${testId}`;
export const getParticipantCookieName = (testId: string) =>
  `${getTestCookieName(testId)}${participantSuffix}`;

type SetTestCookiesParameters = {
  context?: GetServerSidePropsContext<ParsedUrlQuery>;
  testId: string;
  cookies: Record<string, string>;
  setCookie: SetCookie;
  value: {
    participant: string;
    variant: Record<string, string>;
  };
};

export const setTestCookies = ({
  context,
  testId,
  cookies,
  setCookie,
  value,
}: SetTestCookiesParameters) => {
  // expiry date once for in a day and once in 30 days
  const now = dayjs();

  const testCookieName = getTestCookieName(testId);

  setCookie(context, testCookieName, JSON.stringify(value), {
    expires: now.add(1, 'day').toDate(),
    path: '/',
  });

  const participantCookieName = getParticipantCookieName(testId);

  // set or reset participantID
  if (cookies[participantCookieName] !== value.participant) {
    setCookie(context, participantCookieName, value.participant, {
      expires: now.add(30, 'day').toDate(),
      path: '/',
    });
  }
};

type RemoveTestCookieParameters = {
  context?: GetServerSidePropsContext<ParsedUrlQuery>;
  testId: string;
  removeCookie: DestroyCookie;
};

export const removeTestCookies = ({
  context,
  testId,
  removeCookie,
}: RemoveTestCookieParameters) => {
  removeCookie(context, getParticipantCookieName(testId), {
    path: '/',
  });
  removeCookie(context, getTestCookieName(testId), {
    path: '/',
  });
};

export const getAllTestParticipations = (
  cookies: Record<string, string>,
): Array<{
  testId: string;
  participantId: string;
  variant?: Record<string, string>;
}> => {
  const testIds = Object.keys(cookies).reduce((accumulator, key) => {
    if (!key.startsWith(cookiePrefix)) return accumulator;

    const testId = key.replace(cookiePrefix, '').replace(participantSuffix, '');

    if (!accumulator.includes(testId)) {
      accumulator.push(testId);
    }

    return accumulator;
  }, [] as string[]);

  return testIds.map((testId) => {
    const fullCookieKey = `${cookiePrefix}${testId}`;

    if (cookies[fullCookieKey]) {
      const { participant, variant } = JSON.parse(cookies[fullCookieKey]) as {
        participant: string;
        variant: Record<string, string>;
      };

      return { testId, participantId: participant, variant };
    }

    const participantCookieKey = getParticipantCookieName(testId);

    return {
      testId,
      participantId: cookies[participantCookieKey],
    };
  });
};

export const isAbTestParticipant = ({
  testCookie,
  customTestRecognizerFunction,
}: {
  testCookie: string;
  customTestRecognizerFunction?: () => boolean;
}) => {
  if (testCookie) {
    return !!testCookie;
  }
  if (customTestRecognizerFunction) {
    return customTestRecognizerFunction();
  }
  return false;
};
